/**
 * Server errors whose message was written for the technologist.
 *
 * The app server marks them with `extensions.code === 'USER_FACING'` and puts
 * the analyzer's stable code (e.g. NO_REGISTRATION_IMAGE) in
 * `extensions.reason` (server/src/util/errors.util.ts); Hasura forwards
 * remote-schema errors with their extensions intact, so both survive the
 * proxy. Everything else (INTERNAL_SERVER_ERROR etc.) is a system error and
 * gets the generic toast.
 */
export const USER_FACING_CODE = 'USER_FACING' // must match server/src/util/errors.util.ts

interface GraphQLErrorLike {
  message?: unknown
  extensions?: { code?: unknown; reason?: unknown } | null
}

interface UserFacingDetails {
  message: string
  reason: string | undefined
}

function userFacingDetailsOf(e: unknown): UserFacingDetails | undefined {
  const gqlError = e as GraphQLErrorLike | null | undefined
  if (!gqlError || typeof gqlError !== 'object' || !gqlError.extensions) {
    return undefined
  }
  if (gqlError.extensions.code !== USER_FACING_CODE) {
    return undefined
  }
  // an empty message would make a blank toast; treat it as not user-facing
  if (typeof gqlError.message !== 'string' || !gqlError.message) {
    return undefined
  }
  const { reason } = gqlError.extensions
  return {
    message: gqlError.message,
    reason: typeof reason === 'string' && reason ? reason : undefined,
  }
}

/**
 * Message + analyzer reason code of a user-facing error, or undefined when the
 * error is not one.
 *
 * Accepts an @apollo/client ApolloError (`graphQLErrors[]`), a bare GraphQL
 * error object (`result.errors[0]`), or anything else.
 */
export function getUserFacingDetails(
  e: unknown,
): UserFacingDetails | undefined {
  const direct = userFacingDetailsOf(e)
  if (direct !== undefined) {
    return direct
  }
  const gqlErrors = (e as { graphQLErrors?: unknown } | null | undefined)
    ?.graphQLErrors
  if (!Array.isArray(gqlErrors)) {
    return undefined
  }
  const match = gqlErrors.find(
    (gqlError) => userFacingDetailsOf(gqlError) !== undefined,
  )
  return match === undefined ? undefined : userFacingDetailsOf(match)
}

/** The server's user-facing message, or undefined when the error is not one. */
export function getUserFacingMessage(e: unknown): string | undefined {
  return getUserFacingDetails(e)?.message
}

/** The analyzer's stable reason code (extensions.reason), if the server sent one. */
export function getUserFacingReason(e: unknown): string | undefined {
  return getUserFacingDetails(e)?.reason
}

export const isUserFacingError = (e: unknown): boolean =>
  getUserFacingMessage(e) !== undefined

/**
 * Errors whose message has already been put in front of the technologist
 * (inline on the study page, or as a sticky toast). The global error handler
 * still receives them — so they reach the issue tracker — but must not toast
 * them a second time. Also used for analyzer system failures
 * (analysisFailure.ts), which are shown the same way.
 */
const shownToUser = new WeakSet<object>()

export function markUserFacingErrorShown(e: unknown): void {
  if (typeof e === 'object' && e !== null) {
    shownToUser.add(e)
  }
}

export function wasUserFacingErrorShown(e: unknown): boolean {
  return typeof e === 'object' && e !== null && shownToUser.has(e)
}
